/**
 * Уведомления кабинета igra.now.
 *
 * Главное решение здесь — счётчик умеет быть «неизвестен».
 * `unreadCount: number | null`, и `null` значит «спросить не удалось».
 * Ноль в этом месте был бы враньём («всё прочитано»), а старое число —
 * враньём ещё худшим: человек видел бы счётчик, который уже ничего не
 * описывает. Поэтому при неудачном запросе счётчик снимается совсем, а
 * колокольчик остаётся без бейджа.
 *
 * Отметка «прочитано» уходит на сервер (`PATCH /api/notices/{id}/read`,
 * строка в `wp_notice_read`), поэтому переживает перезагрузку. Локально
 * мы её показываем сразу, но при отказе откатываем — иначе человек увидел
 * бы «прочитано» там, где сервер об этом не знает.
 *
 * В списке есть и прочитанные (`new: 0`): прочитанное не пропадает, его можно
 * открыть ещё раз. Открытое уведомление показывается окном поверх страницы.
 */
import { defineStore } from 'pinia'

import { fetchNotifications, markNotificationRead, sanitizeNotificationHtml, toPreview } from '../api/notificationsApi'
import type { NotificationItem, NotificationRaw } from '../types/notifications'

/** Как часто опрашиваем сервер, пока кабинет открыт. */
export const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000

interface NotificationsState {
  items: NotificationItem[]
  /** `null` — счётчик неизвестен (не загружали или запрос упал). */
  unreadCount: number | null
  loading: boolean
  /** Последняя загрузка закончилась отказом. */
  failed: boolean
  open: boolean
  /** Уведомление, открытое в окне. */
  activeId: string | null
  generation: number
  acknowledgedIds: string[]
  pendingReadIds: string[]
}

/** Таймер опроса живёт вне state: он не реактивен и в него нечего смотреть. */
let pollTimer: ReturnType<typeof setInterval> | null = null

function mapNotification(raw: NotificationRaw): NotificationItem {
  const text = typeof raw.text === 'string' ? raw.text : ''

  return {
    id: String(raw.id),
    title: typeof raw.title === 'string' && raw.title.trim() !== '' ? raw.title : (raw.name ?? ''),
    preview: toPreview(text),
    html: sanitizeNotificationHtml(text),
    date: typeof raw.date === 'string' ? raw.date : '',
    isNew: raw.new === 1,
    autoOpen: raw.auto_open === 1,
    badgeCode: raw.type === 'vpn_badge' && typeof raw.badge_code === 'string' ? raw.badge_code : null,
    earnedAt: raw.earned_at ?? null,
  }
}

export const useNotificationsStore = defineStore('notifications-igra', {
  state: (): NotificationsState => ({
    items: [],
    unreadCount: null,
    loading: false,
    failed: false,
    open: false,
    activeId: null,
    generation: 0,
    acknowledgedIds: [],
    pendingReadIds: [],
  }),

  getters: {
    forcedItems(state): NotificationItem[] {
      return state.items.filter((item) => item.autoOpen && item.isNew)
    },
    /** Бейдж рисуется только когда счётчик известен и он не ноль. */
    hasBadge(state): boolean {
      return typeof state.unreadCount === 'number' && state.unreadCount > 0
    },
    /** Число для бейджа; `null` — рисовать нечего. */
    badgeCount(state): number | null {
      return typeof state.unreadCount === 'number' && state.unreadCount > 0 ? state.unreadCount : null
    },
    activeItem(state): NotificationItem | null {
      return state.items.find((item) => item.id === state.activeId) ?? null
    },
    isEmpty(state): boolean {
      return !state.loading && !state.failed && state.items.length === 0
    },
  },

  actions: {
    async load(): Promise<void> {
      // Параллельный запрос ничего не добавит: ручка отдаёт список целиком.
      if (this.loading) return

      const generation = this.generation
      this.loading = true
      try {
        const { items, unreadCount } = await fetchNotifications()
        if (generation !== this.generation) return
        const mapped = [...new Map(items.map((raw) => {
          const item = mapNotification(raw)
          return [item.id, item] as const
        })).values()]
        let locallyRead = 0
        for (const item of mapped) {
          if (item.isNew && (this.acknowledgedIds.includes(item.id) || this.pendingReadIds.includes(item.id))) {
            item.isNew = false
            locallyRead++
          }
        }
        this.items = mapped
        this.unreadCount = Math.max(0, unreadCount - locallyRead)
        this.failed = false
      } catch {
        if (generation !== this.generation) return
        // Ни ноля, ни прошлого числа: счётчик признаётся неизвестным.
        this.failed = true
        this.unreadCount = null
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },

    /**
     * Отмечает уведомление прочитанным на сервере.
     * Показываем сразу, откатываем при отказе.
     */
    async markRead(id: string): Promise<void> {
      const target = this.items.find((item) => item.id === id)
      if (!target || !target.isNew) return

      const generation = this.generation
      this.pendingReadIds.push(id)
      target.isNew = false
      if (typeof this.unreadCount === 'number') {
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }

      try {
        await markNotificationRead(id)
        if (generation !== this.generation) return
        this.acknowledgedIds.push(id)
        const current = this.items.find((item) => item.id === id)
        if (current) current.isNew = false
      } catch {
        if (generation !== this.generation) return
        // Опрос мог заменить items во время запроса: восстанавливаем текущую
        // запись, а не старый объект и не счётчик до появления новых наград.
        const current = this.items.find((item) => item.id === id)
        if (current && !current.isNew) {
          current.isNew = true
          if (this.unreadCount !== null) this.unreadCount++
        }
      } finally {
        if (generation === this.generation) this.pendingReadIds = this.pendingReadIds.filter((pending) => pending !== id)
      }
    },

    /** Открывает уведомление в окне и разом отмечает его прочитанным. */
    async openItem(id: string): Promise<void> {
      this.activeId = id
      await this.markRead(id)
    },

    closeItem(): void {
      this.activeId = null
    },

    openPanel(): void {
      this.open = true
      void this.load()
    },

    closePanel(): void {
      this.open = false
      this.activeId = null
    },

    togglePanel(): void {
      if (this.open) {
        this.closePanel()
      } else {
        this.openPanel()
      }
    },

    /**
     * Опрос — основной способ узнать о новом. Живой канал centrifugo, если он
     * поднимется, только ускоряет обновление (см. layouts/AppLayout.vue);
     * кабинет обязан работать и без него, поэтому таймер безусловный.
     */
    startPolling(): void {
      void this.load()
      if (pollTimer !== null) return

      pollTimer = setInterval(() => {
        void this.load()
      }, NOTIFICATIONS_POLL_INTERVAL_MS)
    },

    stopPolling(): void {
      if (pollTimer === null) return

      clearInterval(pollTimer)
      pollTimer = null
    },

    /** Выход из аккаунта: чужие уведомления не должны пережить смену пользователя. */
    reset(): void {
      this.generation++
      this.acknowledgedIds = []
      this.pendingReadIds = []
      this.stopPolling()
      this.items = []
      this.unreadCount = null
      this.loading = false
      this.failed = false
      this.open = false
      this.activeId = null
    },
  },
})
